/* eslint-disable no-mixed-spaces-and-tabs */
import React, {
	useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, {
	COLORS,
	ICONS,
} from '../general-components/icon';
import {
	MediaItem, PagedMediaItemExisting, PresentedMediaItem, VideoState,
} from '../../stores/reducers/media';
import PlayButton from '../media/controls/play-button';
import MediaVideoTimer from '../general-components/media-video-timer';
import { GreenroomState as GS } from '../../stores/types/types';
import { getVideoTimeAsHHMMSS, isVideo } from '../../utils/utils';
import { videoActionCommands } from '../../utils/video-machine';
import { appLogger } from '../../utils/datadog';
import TooltipWrapper from '../general-components/tooltips/tooltip-wrapper';
import { pageExists } from '../../utils/media';
import useMedia from '../../hooks/use-media';
import { ROOM_STATES } from '../../stores/reducers/settings';
import * as MediaActions from '../../stores/actions/media';
import * as keyListeners from '../../utils/keyboard-handler';
import useShots from '../../hooks/use-shots';
import { useGetMediaControls } from '../../contexts/media-controls';
import UseDeckControls from './use-deck-controls';

const {
	// LOADED,
	// FAIL,
	// TIMING,
	TIMECHANGE,
	MUTE,
	UNMUTE,
	VOLUME,
	PLAY,
	PAUSE,
	SCRUB,
	STOPSCRUBBING,
	// END,
} = videoActionCommands;

const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

interface MediaControlsProps {
    // Define the props for the MediaControls component here
    mediaId: number;
    isEditingFeed: boolean;
    showbuilderMediaItem?: PresentedMediaItem;
    onSelectMediaFeedChange?: React.Dispatch<React.SetStateAction<number | undefined>>;
    isForShotEditorModal?: boolean;
    host?: boolean;
    isDeck?: boolean;
    timeBubbleRef: React.RefObject<HTMLDivElement>;
    updateShowbuilderMedia:Function;
}

export default function MediaControlsUi({
	isEditingFeed,
	showbuilderMediaItem,
	mediaId,
	isForShotEditorModal,
	isDeck,
	timeBubbleRef,
	updateShowbuilderMedia,
	onSelectMediaFeedChange = () => { },
}: MediaControlsProps) {
	// Implement the logic for the MediaControls component here
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const user = useSelector((state: GS) => state.SessionReducer.user);
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const presentedMedia = useSelector((state: GS) => state.MediaReducer.presentedMedia);
	const mediaModalOpen = useSelector((state: GS) => state.MediaReducer.mediaModalOpen);
	const mediaPages = useSelector(
		(state: GS) => state.MediaReducer.mediaPages,
	);
	const shotStartTimestampEntity = useSelector(
		(state: GS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);

	const scrubBarRef = useRef<HTMLInputElement>(null);
	const onMouseMoveDebounce = useRef<NodeJS.Timeout>();
	const onInputDebounce = useRef<NodeJS.Timeout>();
	const [bubblePos, setBubblePos] = useState(0);
	const [mouseoverValue, setMouseoverValue] = useState(0);
	const onChangeDebounce = useRef<NodeJS.Timeout>();
	const forceFireSyncOnAutoplay = useRef<boolean>(false);
	const useShowboard = useSelector((state: GS) => state.SettingsReducer.useShowboard);
	const modalOpen = useSelector((state: GS) => state.ModalReducer.modalOpen);
	const userPaused = useRef(true);

	const {
		canEditOrControlMedia,
		canDeleteMedia,
	} = useMedia();
	const {
		currentShot,
		// isOnCanvas,
		// handleSelectShot,
	} = useShots();

	UseDeckControls({
		mediaId,
		isEditingFeed,
		showbuilderMediaItem,
		updateShowbuilderMedia,
		onSelectMediaFeedChange,
		isForShotEditorModal,
	});

	const mediaControl = useGetMediaControls(`${mediaId}`);

	const send = mediaControl?.send;
	const state = mediaControl?.state;
	const paused = state?.paused ?? true;
	const elapsed = state?.elapsed ?? 0;
	const duration = state?.duration ?? 6;
	const volume = state?.volume ?? 1;
	const muted = state?.muted ?? false;
	const shownVolume = state?.shownVolume ?? 1;
	const nextPage = mediaControl?.nextPage;
	const previousPage = mediaControl?.previousPage;

	const dispatch = useDispatch();
	const mediaItem = useMemo(
		() => media.find((m: MediaItem) => m.id === mediaId),
		[mediaId, media],
	);
	const presentedMediaItem = useMemo(
		() => (isEditingFeed ? showbuilderMediaItem : presentedMedia
			.find((m) => m.mediaId === mediaId)),
		[presentedMedia, mediaId, showbuilderMediaItem, isEditingFeed],
	);
	const syncVidState = useMemo(
		() => presentedMediaItem?.videoState,
		[presentedMediaItem?.videoState],
	);

	const shouldAutoPlay = useMemo((): boolean => {
		if (roomState === ROOM_STATES.EDITING || isEditingFeed) return false;

		// if the first shot has not started playing yet, this is null
		// if `!useShowboard`, this is in classic mode and should autoplay
		if (!shotStartTimestampEntity && useShowboard) return false;

		// if syncVidState.paused is true, has been synced and paused previously
		// if syncVidState is undefined, this is initial load/sync for the room
		if (syncVidState?.paused === true) return false;

		return true;
	}, [roomState, shotStartTimestampEntity, useShowboard, syncVidState]);

	const changePage = useCallback((newPage: number) => {
		appLogger.info('feed-media.tsx changePage', { mediaItem, presentedMediaItem, newPage });

		if (!mediaItem) return;
		const newPageUrl = pageExists(newPage, mediaItem);

		if (
			!presentedMediaItem
			|| !canEditOrControlMedia(mediaItem, user, host, true)
			|| !newPageUrl
		) return;

		// on pagenation, video state is not cleared. We force write new state.
		forceFireSyncOnAutoplay.current = true;

		const newMediaPage = mediaPages.find((page: PagedMediaItemExisting) => page.url === newPageUrl);
		const newVideoState: VideoState = {
			paused: !shouldAutoPlay,
			timeElapsed: 0,
			volume: 1,
		};

		if (newMediaPage) {
			newVideoState.volume = newMediaPage.volume;
			newVideoState.muted = newMediaPage.muted;
		}

		if (isEditingFeed) {
			updateShowbuilderMedia(
				{ ...presentedMediaItem, currentPage: newPage, videoState: newVideoState },
			);
		} else {
			dispatch(MediaActions.setPresentedMediaPage(mediaId, newPage, newVideoState));
		}

		onSelectMediaFeedChange(mediaId);
	}, [
		mediaItem,
		host,
		presentedMediaItem,
		user,
		mediaId,
		shouldAutoPlay,
		isForShotEditorModal,
		showbuilderMediaItem,
		mediaPages,
	]);

	const [userCanControl, userCanDelete] = useMemo(() => [
		mediaItem ? canEditOrControlMedia(mediaItem, user, host, true, roomState) : false,
		mediaItem ? canDeleteMedia(mediaItem, user, host) : false,
	], [host, mediaItem, user, currentShot]);

	useEffect(() => {
		const previousPageLeftKey = () => {
			if (
				!mediaModalOpen
			&& (!modalOpen || isForShotEditorModal)
			&& (userCanControl || userCanDelete)) {
				changePage((presentedMediaItem?.currentPage ?? 1) - 1);
			}
		};

		const nextPageRightKey = () => {
			if (
				!mediaModalOpen
			&& (!modalOpen || isForShotEditorModal)
			&& (userCanControl || userCanDelete)) {
				changePage((presentedMediaItem?.currentPage ?? 1) + 1);
			}
		};

		if (presentedMediaItem && mediaItem && mediaItem.num_pages > 1) {
			keyListeners.addListener(keyListeners.KEYS.KEY_LEFT, previousPageLeftKey);
			keyListeners.addListener(keyListeners.KEYS.KEY_RIGHT, nextPageRightKey);
		}
		return () => {
			keyListeners.removeListener(keyListeners.KEYS.KEY_LEFT, previousPageLeftKey);
			keyListeners.removeListener(keyListeners.KEYS.KEY_RIGHT, nextPageRightKey);
		};
	}, [
		modalOpen,
		isForShotEditorModal,
		mediaModalOpen,
		userCanControl,
		userCanDelete,
		presentedMediaItem,
	]);

	function onMouseMove(e: React.MouseEvent<HTMLInputElement, MouseEvent>) {
		if (!scrubBarRef.current) return;

		e.currentTarget?.blur();

		clearTimeout(onMouseMoveDebounce?.current);
		onMouseMoveDebounce.current = setTimeout(() => {
			if (timeBubbleRef.current && scrubBarRef.current) {
				const scrubRect = scrubBarRef.current.getBoundingClientRect();
				const bubbleWidth = timeBubbleRef.current?.offsetWidth ?? 0;
				const time = (e.nativeEvent.offsetX / scrubRect.width) * duration;
				const pos = (e.clientX - scrubRect.left);

				const clampedAndFlooredTime = Math.floor(clamp(time, 0, duration));

				const halfPercWidth = scrubRect.width / 200;
				const clampedPos = Math.floor(clamp(pos, halfPercWidth, scrubRect.width - halfPercWidth));
				const truePos = clampedPos - (bubbleWidth / 2);

				if (mouseoverValue !== clampedAndFlooredTime) {
					setMouseoverValue(clampedAndFlooredTime);
					setBubblePos(truePos);
				}
			}
		}, 1);
	}
	const onScrubMouseUp = useCallback(() => {
		appLogger.debug('feed-media.tsx onScrubMouseUp');
		if (send) {
			send(STOPSCRUBBING, { from: 'onScrubMouseUp' });
		}
	}, [send]);

	const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.currentTarget?.blur();

		clearTimeout(onInputDebounce?.current);
		onInputDebounce.current = setTimeout(() => {
			if (!e.target.value) {
				return;
			}
			if (Math.floor(mouseoverValue) !== Math.floor(elapsed)) {
				appLogger.debug('feed-media.tsx onInput');
				send?.(SCRUB, { currentTime: mouseoverValue, from: 'onInput' });
				window.addEventListener('mouseup', onScrubMouseUp, { once: true });
			}
		}, 1);
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.currentTarget?.blur();

		clearTimeout(onChangeDebounce.current);
		onChangeDebounce.current = setTimeout(() => {
			appLogger.debug('feed-media.tsx TIMECHANGE scrubbar onChange', { currentTime: elapsed });
			send?.(TIMECHANGE, { currentTime: mouseoverValue, duration, from: 'scrubbar onChange' });
		}, 1);
	};

	const timeScrubCss = useMemo(() => {
		const perc = ((elapsed / duration) * 100) + 0.1;
		return { background: `linear-gradient(to right, #FFFFFF 0%, #FFFFFF ${perc}%, rgba(255, 255, 255, 0.3) ${perc}%, rgba(255, 255, 255, 0.3) 100%)` };
	}, [elapsed, duration]);

	const volumeScrubCss = useMemo(() => ({ background: `linear-gradient(to right, white 0%, white ${muted ? 0 : shownVolume * 100}%, rgba(255, 255, 255, 0.3) ${muted ? 0 : shownVolume * 100}%, rgba(255, 255, 255, 0.3) 100%)` }), [shownVolume, muted]);

	const urlOfCurrentPage = useMemo(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		return imageArray[currentPage - 1] ?? '';
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
	]);

	const currentMediaPage = useMemo((): PagedMediaItemExisting | null => {
		if (urlOfCurrentPage) {
			const mediaPage = mediaPages?.find(
				({ url }) => url === urlOfCurrentPage,
			);

			if (mediaPage) {
				return mediaPage;
			}
		}

		return null;
	}, [mediaPages, urlOfCurrentPage]);

	const currentPageIsVideo = useMemo(() => isVideo(urlOfCurrentPage), [urlOfCurrentPage]);

	// Know user initiated pause state immediately in this component
	const setVideoPausedState = (toPaused: boolean): void => {
		if (toPaused) {
			send?.(PAUSE, { doFireSync: true, from: 'play button' });
			userPaused.current = true;
		} else {
			send?.(PLAY, { doFireSync: true, from: 'play button' });
			userPaused.current = false;
		}
	};

	return (
        <>
    {!currentPageIsVideo
        && <>
            <div className='width-full align-items-center presentation-controls'>
                {presentedMediaItem && (mediaItem?.num_pages ?? 0) > 1
                    && <button
                        className='background-lighter round padding-medium control'
                        data-testid="slideDeckMediaPresentationsControlLeftVisible"
                        disabled={presentedMediaItem?.currentPage === 1} onClick={(e) => {
                        	previousPage?.();
                        	e.currentTarget.blur();
                        }}>
                        <Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={9} color={COLORS.GRAY} />
                    </button>
                }
                <div className="controls-pages">
                    {
                    presentedMediaItem
                    && presentedMediaItem.currentPage
                    && (mediaItem?.num_pages ?? 0) > 1
                    && <span>{presentedMediaItem?.currentPage}/{(mediaItem?.num_pages ?? 0)}</span>
                    }
                </div>
                {presentedMediaItem && (mediaItem?.num_pages ?? 0) > 1
                    && <button
                        onClick={(e) => {
                        	nextPage?.();
                        	e.currentTarget.blur();
                        }}
                        className='background-lighter round padding-medium control'
                        disabled={presentedMediaItem?.currentPage === (mediaItem?.num_pages ?? 0)}
                        data-testid="slideDeckMediaPresentationsControlRightVisible"
                    >
                        <Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={9} color={COLORS.GRAY} />
                    </button>
                }
            </div>
        </>
    }
        <div className="width-full align-items-center media-controls" data-testid='videoMediaControlsVisible'>
            {presentedMediaItem && (mediaItem?.num_pages ?? 0) > 1
                && <>
                    <button
                        onClick={(e) => {
                        	previousPage?.();
                        	e.currentTarget.blur();
                        }}
                        className='height-full control left'
                        disabled={presentedMediaItem.currentPage === 1}
                    >
                        <Icon
                            name={ICONS.KEYBOARD_ARROW_LEFT}
                            size={9}
                            color={presentedMediaItem.currentPage === 1
                            	? COLORS.GRAY
                            	: COLORS.WHITE}
                        />
                    </button>
                </>
            }
            <PlayButton
                paused={paused ?? true}
                videoPlayPause={setVideoPausedState}
                className={'play-button-canvas'}
                isForShotEditorModal={isForShotEditorModal}
            />
            <MediaVideoTimer duration={duration ?? 6} elapsed={elapsed ?? 0} />

            <div className={
                classNames(
                	'align-items-center position-relative flex-grow scrub-wrap',
                	{
                		presenter: !host,
                		deck: isDeck,
                	},
                )}
            >

                    <div
                        ref={timeBubbleRef}
                        className='time-bubble'
                        style={{ left: bubblePos }}
                    >
                        {getVideoTimeAsHHMMSS(mouseoverValue)}
                    </div>

                <input
                    type="range"
                    className="scrubbar"
                    onChange={onChange}
                    onInput={onInput}
                    onMouseMove={onMouseMove}
                    value={elapsed}
                    style={timeScrubCss}
                    max={duration || 0}
                    min={0}
                    step={0.001}
                    ref={scrubBarRef}
                />
            </div>
            {<div className={'align-items-center position-relative padding-left-medium vol-wrap'}>
                {!isForShotEditorModal
                	? <div className={classNames('vol-icon-wrap', { muted })} onClick={() => {
                		appLogger.debug(`feed-media.tsx ${muted ? 'UNMUTE' : 'MUTE'} volume button`, { doFireSync: true });
                		send?.(muted ? UNMUTE : MUTE, {
                			doFireSync: true,
                			from: 'volume button',
                			currentMediaPage,
                		});
                	}}>
                        <Icon className='margin-right-small' name={muted ? ICONS.AUDIO_MUTED : ICONS.SOUND} size={18} color={muted ? COLORS.GRAY : COLORS.WHITE} />
                    </div>
                	: <TooltipWrapper message="Cannot unmute videos in Edit Shot view">
                        <Icon
                            className='margin-right-small margin-top-small'
                            name={muted ? ICONS.AUDIO_MUTED : ICONS.SOUND}
                            size={18}
                            color={muted ? COLORS.GRAY : COLORS.WHITE}
                        />
                    </TooltipWrapper>
                }
                {!isForShotEditorModal
                    && <input
                        type="range"
                        className="volume"
                        onMouseDown={() => {
                        	appLogger.debug('feed-media.tsx VOLUME onMouseDown', { volume, doFireSync: true });
                        	send?.(VOLUME, {
                        		shownVolume,
                        		doFireSync: true,
                        		from: 'volume onMouseDown',
                        		currentMediaPage,
                        	});
                        }}
                        onChange={(e) => {
                        	appLogger.debug('feed-media.tsx VOLUME onChange', { volume, doFireSync: true });
                        	send?.(VOLUME, { shownVolume: Number(e.target.value), from: 'volume onChange' });
                        	e.currentTarget.blur();
                        }}
                        onMouseUp={(e) => {
                        	appLogger.debug('feed-media.tsx VOLUME onMouseUp', { volume, doFireSync: true });
                        	send?.(VOLUME, {
                        		shownVolume,
                        		doFireSync: true,
                        		from: 'volume onMouseUp',
                        		currentMediaPage,
                        	});
                        	e.currentTarget.blur();
                        }}
                        value={muted ? 0 : shownVolume}
                        step={0.01}
                        min={0}
                        max={1}
                        style={volumeScrubCss}
                    >
                    </input>
                }
            </div>}
            {presentedMediaItem && (mediaItem?.num_pages ?? 0) > 1
                && <>
                    <button
                        onClick={(e) => {
                        	nextPage?.();
                        	e.currentTarget.blur();
                        }}
                        className='height-full control right'
                        disabled={
                            presentedMediaItem?.currentPage === mediaItem?.num_pages
                        }
                    >
                        <Icon
                            name={ICONS.KEYBOARD_ARROW_RIGHT}
                            size={9}
                            color={
                                presentedMediaItem?.currentPage === mediaItem?.num_pages
                                	? COLORS.GRAY
                                	: COLORS.WHITE
                            }
                        />
                    </button>
                    <div className="controls-pages">
                        {
                            presentedMediaItem.currentPage
                            && (mediaItem?.num_pages ?? 0) > 1
                            && <span>
                                    {presentedMediaItem?.currentPage}/{mediaItem?.num_pages}
                            </span>
                        }
                    </div>
                </>
            }
        </div>
        </>
	);
}
